import { supabase, isSupabaseConfigured } from './supabase';
import { getRealogySupabase, isRealogyConfigured } from './realogySupabase';
import { getSupabaseServer } from './supabase-server';
import { parseListingRemarks } from './realogyHelpers';

// Property types to always exclude (rentals / leases)
const EXCLUDED_LEASE_TYPES = ['Residential Lease', 'Commercial Lease'];

// Type lists for the property-type hubs. Shared with the city helpers below so
// the set of cities a hub links to can't drift from the set it can show.
const LAND_PROPERTY_TYPES = ['RES Vacant Land', 'Commercial Land'];
const COMMERCIAL_SUB_TYPES = ['Commercial', 'Apartment', 'Multi Family', 'Duplex', 'Triplex'];
// Condominium only. Townhouse is a distinct sub-type and a distinct search —
// mixing them would make the condo hub answer neither query cleanly.
const CONDO_SUB_TYPES = ['Condominium'];

/** Listings per page on the property-type hubs. */
export const HUB_PAGE_SIZE = 48;

export interface HubListings {
  listings: MLSProperty[];
  total: number;
}

// Simple in-memory cache with TTL for expensive dropdown queries
const memCache = new Map<string, { data: any; expiry: number }>();
function getCached<T>(key: string, ttlMs: number, fn: () => Promise<T>): Promise<T> {
  const cached = memCache.get(key);
  if (cached && Date.now() < cached.expiry) return Promise.resolve(cached.data as T);
  return fn().then((data) => {
    memCache.set(key, { data, expiry: Date.now() + ttlMs });
    return data;
  });
}

// Raw data from the mls_properties table (RESO-compliant schema).
// `listing_id` is kept as an optional alias for `mls_number` so any
// legacy callers reading row.listing_id still work.
interface GraphQLListing {
  id: string;
  listing_id?: string;
  mls_number?: string;
  status: string;
  list_price: number | null;
  sold_price: number | null;
  sold_date?: string | null;
  address: string | null;
  street_number: string | null;
  street_name: string | null;
  street_suffix: string | null;
  unit_number: string | null;
  city: string | null;
  state: string | null;
  county: string | null;
  zip_code: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  bathrooms_total: number | null;
  bathrooms_full: number | null;
  bathrooms_half: number | null;
  bathrooms_three_quarter: number | null;
  square_feet: number | null;
  living_area: number | null;
  lot_size: number | null;
  lot_size_square_feet: number | null;
  lot_size_acres: number | null;
  year_built: string | null;
  property_type: string | null;
  property_sub_type: string | null;
  listing_date: string | null;
  // NOTE: there is no close_date column on mls_properties — the closing date is
  // `sold_date`. Two separate outages have come from querying close_date (see
  // 0517599 and the getListings fix); don't reintroduce it here.
  original_list_price: number | null;
  description: string | null;
  subdivision_name: string | null;
  mls_area_major: string | null;
  mls_area_minor: string | null;
  neighborhood?: string | null;
  preferred_photo?: string | null;
  photos?: string[] | null;
  media: string[] | null;
  latitude: number | null;
  longitude: number | null;
  agent_name?: string | null;
  agent_email?: string | null;
  list_office_name: string | null;
  list_office_mls_id?: string | null;
  list_agent_mls_id: string | null;
  list_agent_full_name: string | null;
  list_agent_first_name?: string | null;
  list_agent_last_name?: string | null;
  list_agent_cell_phone?: string | null;
  list_agent_direct_phone?: string | null;
  list_agent_url?: string | null;
  co_list_agent_mls_id: string | null;
  co_list_agent_full_name?: string | null;
  buyer_agent_mls_id: string | null;
  buyer_agent_full_name?: string | null;
  co_buyer_agent_mls_id: string | null;
  virtual_tour_url: string | null;
  video_urls?: string[] | null;
  furnished: string | null;
  fireplace_yn: boolean | null;
  fireplace_features: string[] | null;
  fireplaces_total?: number | null;
  fireplace_total?: number | null;
  cooling: string[] | null;
  heating: string[] | null;
  laundry_features: string[] | null;
  attached_garage_yn: boolean | null;
  parking_features: string[] | null;
  association_amenities: string[] | null;
  open_house_date?: string | null;
  open_house_start_time?: string | null;
  open_house_end_time?: string | null;
  open_house_remarks?: string | null;
  created_at: string;
  updated_at: string;
}

// Normalized property interface used throughout the app
export interface MLSProperty {
  id: string;
  mls_number: string;
  status: string;
  list_price: number | null;
  sold_price: number | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  neighborhood: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  bathrooms_full: number | null;
  bathrooms_half: number | null;
  bathrooms_three_quarter: number | null;
  square_feet: number | null;
  lot_size: number | null;
  year_built: number | null;
  property_type: string | null;
  listing_date: string | null;
  sold_date: string | null;
  days_on_market: number | null;
  description: string | null;
  features: Record<string, any>;
  agent_name: string | null;
  agent_email: string | null;
  photos: string[];
  video_urls: string[];
  latitude: number | null;
  longitude: number | null;
  // Additional property details
  subdivision_name: string | null;
  mls_area_minor: string | null;
  furnished: string | null;
  fireplace_yn: boolean | null;
  fireplace_features: string[] | null;
  fireplace_total: number | null;
  cooling: string[] | null;
  heating: string[] | null;
  laundry_features: string[] | null;
  attached_garage_yn: boolean | null;
  parking_features: string[] | null;
  association_amenities: string[] | null;
  virtual_tour_url: string | null;
  list_agent_mls_id: string | null;
  list_agent_full_name: string | null;
  co_list_agent_mls_id: string | null;
  buyer_agent_mls_id: string | null;
  co_buyer_agent_mls_id: string | null;
  list_office_name: string | null;
  list_office_phone: string | null;
  open_house_date: string | null;
  open_house_start_time: string | null;
  open_house_end_time: string | null;
  open_house_remarks: string | null;
  created_at: string;
  updated_at: string;
  // True for partner/affiliate rows sourced from realogy_listings. These do NOT
  // live under /real-estate-for-sale — their canonical page is the market-leaders
  // partner listing route. See getListingHref.
  is_realogy?: boolean;
  // True when the RAW property_type is a lease type. `property_type` above
  // collapses to property_sub_type when one exists ('Single Family Residence'),
  // which loses the fact that the listing is a rental — so capture it here at
  // transform time. Keyword searches deliberately return rentals, so cards need
  // this to label them.
  is_rental?: boolean;
}

// A listing is sold if the feed says Sold/Closed. transformListing normalizes
// 'Closed' to 'Sold' (and auto-closes past close_dates), but raw rows and the
// Realogy transform still produce 'Closed' — match either.
export function isSoldStatus(status: string | null | undefined): boolean {
  const s = status?.trim().toLowerCase();
  return s === 'sold' || s === 'closed';
}

// The feed files PDFs (floor plans, disclosures) alongside photos; the image
// optimizer rejects them with a 400, so keep them out of the photo list.
const DOCUMENT_URL = /\.(pdf|docx?|xlsx?|pptx?|txt)(\?|#|$)/i;
function isImageUrl(url: string): boolean {
  return !DOCUMENT_URL.test(url);
}

// The feed's virtual_tour_url is free text, not a URL: a bare domain
// ("www.homestories.ai/..."), a bit.ly link, a whole <iframe> snippet, or a
// label. Framed as-is, a relative value resolves under /real-estate-for-sale/
// and 404s. Reduce it to an absolute URL or drop it.
const EMBED_SRC = /src=["']([^"']+)["']/i;
const DOMAIN_SHAPED = /^[\w-]+(\.[\w-]+)+(?::\d+)?([/?#]|$)/;
export function normalizeVirtualTourUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  let value = raw.trim();
  const embed = value.match(EMBED_SRC);
  if (embed) value = embed[1];
  // Some values are a URL with the tail of an embed snippet still attached
  value = value.replace(/&amp;/g, '&').split(/[\s"'<>]/)[0];
  if (value.startsWith('//')) value = `https:${value}`;
  else if (!/^https?:\/\//i.test(value)) {
    if (!DOMAIN_SHAPED.test(value)) return null;
    value = `https://${value}`;
  }
  try {
    return new URL(value).href;
  } catch {
    return null;
  }
}

// "March 14, 2024" from a feed date. Splits the YYYY-MM-DD parts by hand and
// builds a LOCAL date: `new Date('2024-03-14')` is parsed as UTC midnight, which
// renders as March 13 anywhere west of Greenwich. Locale is pinned to en-US so
// the server and client render the same string and hydration stays quiet.
export function formatListingDate(date: string | null | undefined): string | null {
  if (!date) return null;
  const parts = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(date);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// Rentals/leases are identified by the raw property_type column.
export function isRentalPropertyType(propertyType: string | null | undefined): boolean {
  if (!propertyType) return false;
  const t = propertyType.trim().toLowerCase();
  return EXCLUDED_LEASE_TYPES.some((lease) => lease.toLowerCase() === t);
}

// Build listing URL — uses MLS number when available, falls back to database ID
/**
 * Build an SEO-friendly listing slug from address components plus the
 * MLS number. Format:
 *   address-city-state-zip-MLSNUMBER
 * e.g. "525-s-original-street-unit-d-aspen-co-81611-167338"
 *
 * The MLS number is always the trailing segment so the resolver can
 * extract it back out reliably regardless of how messy the address is.
 * Falls back to bare MLS number / database id when address fields are
 * missing — never produces an empty slug.
 */
export function buildListingSlug(listing: {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  mls_number?: string | null;
  id?: string | null;
}): string {
  const id = listing.mls_number || listing.id || '';
  // mls_properties.address is the full "123 Main St, Aspen, CO 81611"
  // string. Strip city/state/zip out of it so we don't end up with
  // duplicates like "9888-castle-creek-road-aspen-co-81611-aspen-co-81611-185713"
  // when the slug builder re-appends them below.
  const street = listing.address
    ? listing.address.split(',')[0].trim()
    : '';
  const parts: string[] = [];
  if (street) parts.push(toAddressSlug(street));
  if (listing.city) parts.push(toAddressSlug(listing.city));
  if (listing.state) parts.push(listing.state);
  if (listing.zip_code) parts.push(String(listing.zip_code));
  if (id) parts.push(String(id));
  return parts
    .filter(Boolean)
    .join('-')
    .toLowerCase()
    // Collapse any double hyphens introduced by empty trim results
    .replace(/-+/g, '-');
}

export function getListingHref(listing: {
  id: string;
  mls_number?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  is_realogy?: boolean;
}): string {
  // Partner/affiliate listings are not served under /real-estate-for-sale (that
  // route only queries mls_properties). Their canonical page is the market-leaders
  // partner route, keyed by the same address slug the partner cards/agent tiles
  // link to (see toAddressSlug + getRealogyListingByAddressSlug).
  if (listing.is_realogy) {
    const slug = toAddressSlug(listing.address);
    if (slug) return `/affiliated-partners/market-leaders/listings/${slug}`;
  }
  return `/real-estate-for-sale/${buildListingSlug(listing)}`;
}

// Resolve listing by slug — handles both the new address-based format
// (address-city-state-zip-MLSNUMBER) and any legacy URL still
// floating around (bare MLS number, database id, Realogy slug,
// Realogy address slug).
export async function getListingBySlug(slug: string): Promise<MLSProperty | null> {
  // 1. Try the slug verbatim as an MLS number / database id. Handles
  //    legacy /listings/167338 URLs and the rare case of a slug that's
  //    just an id with no address prefix.
  const direct = await getListingByMlsNumber(slug) || await getListingById(slug);
  if (direct) return direct;

  // 2. New address-based format: extract the trailing alphanumeric
  //    segment as the MLS number / id and look it up.
  const trailing = slug.match(/-([A-Za-z0-9]+)$/);
  if (trailing) {
    const trailingId = trailing[1];
    const byTrailing = await getListingByMlsNumber(trailingId) || await getListingById(trailingId);
    if (byTrailing) return byTrailing;
  }

  // 3. Fall through to Realogy resolvers (entity_id slugs +
  //    address-only slugs used by the partner-listing template).
  return await getRealogyListingBySlug(slug) || await getRealogyListingByAddressSlug(slug);
}

// Deduplicate listings by mls_number, preferring rows with more complete data
function deduplicateListings(listings: MLSProperty[]): MLSProperty[] {
  const seen = new Map<string, MLSProperty>();
  for (const listing of listings) {
    const key = listing.mls_number || listing.id;
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, listing);
    } else {
      // Strongly prefer rows with a valid updated_at (rows with null updated_at
      // have stale data including outdated prices from before price changes).
      // Then break ties by data completeness (address, photos).
      const score = (l: MLSProperty) =>
        (l.updated_at ? 100 : 0) + (l.address ? 1 : 0) + (l.photos?.length || 0);
      if (score(listing) > score(existing)) {
        seen.set(key, listing);
      }
    }
  }
  return Array.from(seen.values());
}

// Transform graphql_listings row to MLSProperty format
function transformListing(row: GraphQLListing): MLSProperty {
  // Extract photos from media — handles JSON arrays, JSON strings, and string arrays
  const photos: string[] = [];
  if (row.preferred_photo) {
    let pp = row.preferred_photo;
    if (pp.startsWith('//')) pp = `https:${pp}`;
    photos.push(pp);
  }

  // The dedicated `photos` text[] column is the primary photo source
  // populated by the current MLS sync. Items may be plain URL strings
  // (legacy) or `{url, order, caption}` objects (new sync shape). Walk
  // both forms before falling back to the media[] scrape below.
  const rawPhotos: any = (row as any).photos;
  if (Array.isArray(rawPhotos)) {
    const ordered = [...rawPhotos].sort((a, b) => {
      const ao = typeof a === 'object' && a !== null ? (a.order ?? a.Order ?? 0) : 0;
      const bo = typeof b === 'object' && b !== null ? (b.order ?? b.Order ?? 0) : 0;
      return ao - bo;
    });
    for (const p of ordered) {
      let url: string | undefined;
      if (typeof p === 'string') {
        url = p;
      } else if (p && typeof p === 'object') {
        url = p.url || p.URL || p.MediaURL || p.MediaUrl || p.photoUrl || p.src;
      }
      if (url && typeof url === 'string') {
        if (url.startsWith('//')) url = `https:${url}`;
        if (!photos.includes(url)) photos.push(url);
      }
    }
  }

  let mediaItems: any[] = [];
  if (row.media) {
    let mediaToParse: any = row.media;

    // If it's a string, try to parse it as JSON
    if (typeof mediaToParse === 'string') {
      try {
        mediaToParse = JSON.parse(mediaToParse);
      } catch {
        // Not valid JSON — try to extract URLs directly via regex
        const urlMatches = (mediaToParse as string).match(/(?:https?:)?\/\/[^\s"',}]+/g);
        if (urlMatches) {
          for (let url of urlMatches) {
            if (url.startsWith('//')) url = `https:${url}`;
            if (!photos.includes(url)) photos.push(url);
          }
        }
      }
    }

    // At this point mediaToParse should be a parsed JSON value
    if (Array.isArray(mediaToParse)) {
      mediaItems = mediaToParse;
    } else if (mediaToParse && typeof mediaToParse === 'object') {
      // Might be a single media object or a wrapper like {items: [...]}
      if (Array.isArray(mediaToParse.items)) {
        mediaItems = mediaToParse.items;
      } else if (Array.isArray(mediaToParse.media)) {
        mediaItems = mediaToParse.media;
      } else {
        // Single media object
        mediaItems = [mediaToParse];
      }
    }
  }
  // Collect video URLs in the same media[] pass so the listing-detail
  // video tab can render any RESO Video-category items (Brightcove etc.)
  const videoUrlsFromMedia: string[] = [];

  for (const mediaItem of mediaItems) {
    try {
      let parsed: any = mediaItem;
      if (typeof mediaItem === 'string') {
        // If it looks like a URL, use it directly; otherwise try JSON.parse
        if (mediaItem.startsWith('http') || mediaItem.startsWith('//')) {
          parsed = mediaItem;
        } else {
          parsed = JSON.parse(mediaItem);
        }
      }

      // Category is provided as either the RESO `MediaCategory` or the
      // newer lowercase `category` (e.g. "Document", "Video", "Photo").
      const category = (parsed && typeof parsed === 'object')
        ? (parsed.MediaCategory || parsed.category || '')
        : '';

      // Video items: MediaHTML / html carries the player embed (often a
      // full <iframe src="..."> string), MediaURL / url is usually null.
      // Pull the src URL out of any iframe HTML so video_urls only ever
      // holds clean player URLs — the renderer wraps them in its own
      // iframe and would break if we passed it nested HTML.
      if (parsed && typeof parsed === 'object' && /video/i.test(category)) {
        const embed: string | undefined =
          parsed.MediaURL || parsed.url
          || parsed.MediaHTML || parsed.mediaHtml || parsed.html;
        let vurl: string | undefined;
        if (typeof embed === 'string' && embed.length > 0) {
          if (/^<\s*iframe\b/i.test(embed.trim())) {
            const m = embed.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
            vurl = m ? m[1] : undefined;
          } else {
            vurl = embed;
          }
        }
        if (vurl) {
          if (vurl.startsWith('//')) vurl = `https:${vurl}`;
          if (!videoUrlsFromMedia.includes(vurl)) videoUrlsFromMedia.push(vurl);
        }
        continue;
      }

      // Document / non-photo categories: skip, otherwise survey PDFs
      // and disclosures get hoisted into the photo gallery.
      if (parsed && typeof parsed === 'object' && /document|brochure|disclosure|floor\s*plan|virtual\s*tour/i.test(category)) {
        continue;
      }

      let url: string | undefined;
      if (typeof parsed === 'string') {
        // Direct URL string
        url = parsed;
      } else if (parsed && typeof parsed === 'object') {
        // Check all common key names for media URLs
        url = parsed.MediaURL || parsed.MediaUrl || parsed.mediaUrl || parsed.mediaURL
          || parsed.media_url || parsed.url || parsed.URL || parsed.src
          || parsed.photo_url || parsed.photoUrl || parsed.image_url || parsed.imageUrl;
      }
      if (url && typeof url === 'string') {
        // Guard against non-image URLs (PDFs etc.) sneaking in when the
        // upstream sync omitted MediaCategory.
        if (/\.(pdf|docx?|xlsx?)(\?|$)/i.test(url)) continue;
        // Fix protocol-relative URLs (e.g. "//cdn.example.com/...")
        if (url.startsWith('//')) {
          url = `https:${url}`;
        }
        if (!photos.includes(url)) {
          photos.push(url);
        }
      }
    } catch {
      // Skip invalid media items
    }
  }

  // Debug: log when media data exists but no photos were extracted
  if (row.media && photos.length === 0) {
    const rawMedia: any = row.media;
    const preview = typeof rawMedia === 'string' ? rawMedia.slice(0, 300) : JSON.stringify(rawMedia).slice(0, 300);
    console.error(`[Media Debug] Listing ${(row.mls_number || row.listing_id) || row.id}: media exists (type=${typeof rawMedia}, isArray=${Array.isArray(rawMedia)}) but 0 photos extracted. Preview: ${preview}`);
  }

  // Calculate days on market
  let daysOnMarket: number | null = null;
  if (row.listing_date) {
    const listDate = new Date(row.listing_date);
    const endDate = row.sold_date ? new Date(row.sold_date) : new Date();
    daysOnMarket = Math.floor((endDate.getTime() - listDate.getTime()) / (1000 * 60 * 60 * 24));
  }

  // Extract MLS number: prefer listing_id, fall back to description ("MLS# 285075 ...")
  // or photo URL path ("/PACMLS/285075/")
  let mlsNumber: string = (row.mls_number || row.listing_id) || '';
  if (!mlsNumber && row.description) {
    const match = row.description.match(/MLS#\s*(\d+)/i);
    if (match) mlsNumber = match[1];
  }
  if (!mlsNumber && photos.length > 0) {
    const match = photos[0].match(/\/PACMLS\/(\d+)\//);
    if (match) mlsNumber = match[1];
  }

  // Auto-close: if the closing date is in the past, mark as Sold
  let status = row.status;
  if (row.sold_date) {
    const closeDate = new Date(row.sold_date);
    if (closeDate.getTime() < Date.now()) {
      status = 'Sold';
    }
  }
  // Normalize: always show "Closed" as "Sold"
  if (status?.toLowerCase() === 'closed') {
    status = 'Sold';
  }

  return {
    id: row.id,
    mls_number: mlsNumber,
    status,
    list_price: row.list_price || row.sold_price,
    sold_price: row.sold_price,
    address: row.address || [row.street_number, row.street_name].filter(Boolean).join(' ') || null,
    city: row.city,
    state: row.state,
    zip_code: row.zip_code,
    neighborhood: row.neighborhood || row.subdivision_name || row.mls_area_minor || null,
    bedrooms: row.bedrooms,
    bathrooms: row.bathrooms_total ?? row.bathrooms,
    bathrooms_full: row.bathrooms_full,
    bathrooms_half: row.bathrooms_half,
    bathrooms_three_quarter: row.bathrooms_three_quarter,
    square_feet: row.square_feet || row.living_area,
    lot_size: row.lot_size_acres ?? row.lot_size ?? (row.lot_size_square_feet ? row.lot_size_square_feet / 43560 : null),
    year_built: row.year_built ? parseInt(row.year_built, 10) : null,
    property_type: row.property_sub_type || row.property_type,
    is_rental: isRentalPropertyType(row.property_type),
    listing_date: row.listing_date,
    sold_date: row.sold_date ?? null,
    days_on_market: daysOnMarket,
    description: row.description,
    features: {},
    // Agent contact info: prefer the canonical list_agent_full_name from
    // RESO, fall back to the legacy `agent_name` free-text field.
    agent_name: row.list_agent_full_name || row.agent_name || null,
    agent_email: row.agent_email || null,
    photos: photos.filter(isImageUrl),
    // Merge any video URLs the dedicated column has with the ones we
    // pulled out of the media[] array's Video-category items.
    video_urls: [
      ...(Array.isArray(row.video_urls) ? row.video_urls : []),
      ...videoUrlsFromMedia.filter((u) => !(row.video_urls || []).includes(u)),
    ],
    latitude: row.latitude != null ? Number(row.latitude) || null : null,
    longitude: row.longitude != null ? Number(row.longitude) || null : null,
    // Additional property details
    subdivision_name: row.subdivision_name,
    mls_area_minor: row.mls_area_minor,
    furnished: row.furnished,
    fireplace_yn: row.fireplace_yn,
    fireplace_features: row.fireplace_features,
    // RESO column is `fireplaces_total` (plural). Older mirrors used
    // `fireplace_total`; read either for safety.
    fireplace_total: row.fireplaces_total ?? row.fireplace_total ?? null,
    cooling: row.cooling,
    heating: row.heating,
    laundry_features: row.laundry_features,
    attached_garage_yn: row.attached_garage_yn,
    parking_features: row.parking_features,
    association_amenities: row.association_amenities,
    virtual_tour_url: normalizeVirtualTourUrl(row.virtual_tour_url),
    list_agent_mls_id: row.list_agent_mls_id,
    list_agent_full_name: row.list_agent_full_name || null,
    co_list_agent_mls_id: row.co_list_agent_mls_id,
    buyer_agent_mls_id: row.buyer_agent_mls_id,
    co_buyer_agent_mls_id: row.co_buyer_agent_mls_id,
    list_office_name: row.list_office_name || null,
    list_office_phone: null,
    // open_house_* columns aren't present in mls_properties yet — coalesce
    // to null so the MLSProperty type (string | null) stays satisfied.
    open_house_date: row.open_house_date ?? null,
    open_house_start_time: row.open_house_start_time ?? null,
    open_house_end_time: row.open_house_end_time ?? null,
    open_house_remarks: row.open_house_remarks ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export type SortOption = 'newest' | 'price_low' | 'price_high' | 'beds_low' | 'beds_high';

export interface ListingsFilters {
  status?: string;
  propertyType?: string;      // Main property type (e.g., Residential, Commercial Sale)
  propertySubType?: string;   // Property subtype (e.g., Single Family Residence, Condominium)
  cities?: string[];
  neighborhood?: string;
  minPrice?: number;
  maxPrice?: number;
  minBeds?: number;
  maxBeds?: number;
  minBaths?: number;
  maxBaths?: number;
  minSqft?: number;
  maxSqft?: number;
  // Keyword search (MLS# or address)
  keyword?: string;
  // Agent/office filtering (Our Listings Only)
  agentMlsIds?: string[];
  agentNames?: string[];  // Fallback: also match by agent full name
  officeNames?: string[];  // Match by listing office name
  // Filters from MLS Configuration
  excludedPropertyTypes?: string[];      // Excluded main property types
  excludedPropertySubTypes?: string[];   // Excluded property subtypes
  allowedCities?: string[];  // If set, only show listings from these cities
  excludedStatuses?: string[];
  allowedStatuses?: string[];   // If set, only show listings with these statuses
  // When true, show ONLY effectively-sold listings (raw Sold/Closed status or a
  // past close_date). When false/undefined, effectively-sold listings are
  // excluded so they never leak into an active search. See transformRow's
  // display-time auto-close for why close_date matters here.
  soldOnly?: boolean;
  // Sorting
  sort?: SortOption;
}

export interface ListingsResult {
  listings: MLSProperty[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export async function getListings(
  page: number = 1,
  pageSize: number = 24,
  filters: ListingsFilters = {}
): Promise<ListingsResult> {
  if (!isSupabaseConfigured()) {
    return { listings: [], total: 0, page, pageSize, totalPages: 0 };
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  // Use graphql_listings materialized view (~3K indexed rows) instead of
  // graphql_listings view (~100K+ rows with COALESCE preventing index usage)
  let query = supabase
    .from('mls_properties')
    .select('*', { count: 'estimated' });

  // Apply filters
  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.propertyType) {
    // ilike with no wildcards is an exact match but case-insensitive. The feed is
    // inconsistent about 'RES Vacant Land' vs 'Res Vacant Land', and .eq() on the
    // wrong casing silently returns zero rows.
    query = query.ilike('property_type', filters.propertyType);
  }
  if (filters.propertySubType) {
    query = query.ilike('property_sub_type', filters.propertySubType);
  }
  if (filters.cities && filters.cities.length > 0) {
    // Use ilike for single city (case-insensitive), in() for multiple
    // Avoids multiple .or() calls which can conflict in PostgREST
    if (filters.cities.length === 1) {
      query = query.ilike('city', filters.cities[0]);
    } else {
      query = query.in('city', filters.cities);
    }
  }
  if (filters.neighborhood) {
    // Search subdivision_name or mls_area_minor
    query = query.or(`subdivision_name.ilike.%${filters.neighborhood}%,mls_area_minor.ilike.%${filters.neighborhood}%`);
  }
  if (filters.minPrice) {
    query = query.gte('list_price', filters.minPrice);
  }
  if (filters.maxPrice) {
    query = query.lte('list_price', filters.maxPrice);
  }
  if (filters.minBeds) {
    query = query.gte('bedrooms', filters.minBeds);
  }
  if (filters.maxBeds) {
    query = query.lte('bedrooms', filters.maxBeds);
  }
  if (filters.minBaths) {
    query = query.gte('bathrooms_total', filters.minBaths);
  }
  if (filters.maxBaths) {
    query = query.lte('bathrooms_total', filters.maxBaths);
  }
  if (filters.minSqft) {
    query = query.gte('square_feet', filters.minSqft);
  }
  if (filters.maxSqft) {
    query = query.lte('square_feet', filters.maxSqft);
  }
  if (filters.keyword) {
    const kw = filters.keyword.trim();
    const isNumeric = /^\d+$/.test(kw);
    if (isNumeric) {
      // Search listing_id (primary) and also description (fallback for rows
      // where ListingId is empty but MLS# appears in PublicRemarks)
      query = query.or(`mls_number.eq.${kw},description.ilike.%MLS# ${kw}%`);
    } else {
      // Search UnparsedAddress and street_name (fallback for rows where
      // UnparsedAddress is NULL but street components exist)
      query = query.or(`address.ilike.%${kw}%,street_name.ilike.%${kw}%`);
    }
  }

  // Filter by team agent MLS IDs, names, and/or office names (Our Listings Only)
  if ((filters.agentMlsIds && filters.agentMlsIds.length > 0) || (filters.agentNames && filters.agentNames.length > 0) || (filters.officeNames && filters.officeNames.length > 0)) {
    // mls_properties now exposes the full RESO column set, so use the
    // canonical *_mls_id / *_full_name / *_office_name fields directly.
    const idConditions = (filters.agentMlsIds || [])
      .map((id) => `list_agent_mls_id.eq.${id},co_list_agent_mls_id.eq.${id},buyer_agent_mls_id.eq.${id}`)
      .join(',');
    const nameConditions = (filters.agentNames || [])
      .map((name) => `list_agent_full_name.ilike.%${name}%`)
      .join(',');
    const officeConditions = (filters.officeNames || [])
      .map((name) => `list_office_name.ilike.%${name}%`)
      .join(',');
    const allConditions = [idConditions, nameConditions, officeConditions].filter(Boolean).join(',');
    query = query.or(allConditions);
  }

  // Always exclude lease/rental property types (also include NULL property_type rows)
  if (!filters.keyword) {
    query = query.or(`property_type.not.in.(${EXCLUDED_LEASE_TYPES.join(',')}),property_type.is.null`);
  }

  // Apply filters from MLS Configuration
  // Skip type exclusions for keyword searches — user is looking for a specific listing
  // A type the visitor explicitly asked for must never also be excluded: the type
  // filter and the exclusion list would contradict each other and the search would
  // always come back empty.
  const excludedPropertyTypes = (filters.excludedPropertyTypes || []).filter(
    (t) => t.toLowerCase() !== filters.propertyType?.toLowerCase()
  );
  if (excludedPropertyTypes.length > 0 && !filters.keyword) {
    // Use or() to also include rows where property_type is NULL
    query = query.or(`property_type.not.in.(${excludedPropertyTypes.join(',')}),property_type.is.null`);
  }
  if (filters.excludedPropertySubTypes && filters.excludedPropertySubTypes.length > 0 && !filters.keyword) {
    if (filters.agentMlsIds?.length || filters.agentNames?.length || filters.officeNames?.length) {
      query = query.not('property_sub_type', 'in', `(${filters.excludedPropertySubTypes.join(',')})`);
    } else {
      query = query.or(`property_sub_type.not.in.(${filters.excludedPropertySubTypes.join(',')}),property_sub_type.is.null`);
    }
  }
  // If allowedCities is set, only show listings from those cities
  // Skip for keyword searches — user is looking for a specific listing
  if (filters.allowedCities && filters.allowedCities.length > 0 && !filters.keyword) {
    query = query.in('city', filters.allowedCities);
  }
  if (filters.allowedStatuses && filters.allowedStatuses.length > 0 && !filters.keyword) {
    // Only show listings with these specific statuses (excludes NULL status rows)
    query = query.in('status', filters.allowedStatuses);
  } else if (filters.excludedStatuses && filters.excludedStatuses.length > 0 && !filters.keyword && !filters.soldOnly) {
    query = query.not('status', 'in', `(${filters.excludedStatuses.join(',')})`);
  }

  // Off-market listings (expired, withdrawn, canceled) were pulled from the MLS
  // and 404 on their detail page, so they must not appear in ANY result set —
  // including keyword searches, which skip every status filter above so that
  // someone looking up a specific address still finds it whether it is active
  // or sold. That blanket skip also let withdrawn listings through, and a
  // search result linking to a 404 is worse than no result at all. Sold/Closed
  // is deliberately NOT in this list: those keep a page.
  const offMarketList = OFF_MARKET_STATUS_VALUES.map((s) => `"${s}"`).join(',');
  query = query.or(`status.is.null,status.not.in.(${offMarketList})`);

  // "Sold" is a display-time status: transformListing labels a listing Sold when
  // its raw status is Sold/Closed OR its closing date has already passed. The
  // status filters above only see the RAW status, so an Active/Pending row with
  // a past closing date would slip through and render as "Sold". Mirror the
  // auto-close rule here so default searches never surface effectively-sold
  // listings, and an explicit Sold filter returns exactly those. Skipped for
  // keyword searches (the user is looking up a specific listing, sold or not).
  //
  // The closing date lives in `sold_date`. mls_properties has NO close_date
  // column — naming it here returned Postgres 42703 and failed the whole query,
  // so every non-keyword search silently came back empty.
  if (!filters.keyword) {
    const nowIso = new Date().toISOString();
    if (filters.soldOnly) {
      query = query.or(`status.in.(Sold,Closed),sold_date.lt.${nowIso}`);
    } else {
      query = query.or(`sold_date.is.null,sold_date.gte.${nowIso}`);
    }
  }

  // A keyword search returns sold listings alongside active ones on purpose —
  // someone typing an address should find the property either way — so lead
  // with what is still on the market. sold_date is NULL for anything unsold,
  // and nullsFirst floats that whole group to the top. This is the PRIMARY
  // sort, so the chosen sort below still orders listings within each group:
  // every active listing ties on NULL and falls back to it, and sold listings
  // come back most-recent-sale-first.
  if (filters.keyword) {
    query = query.order('sold_date', { ascending: false, nullsFirst: true });
  }

  // Apply sorting
  const sort = filters.sort || 'newest';
  switch (sort) {
    case 'price_low':
      query = query.order('list_price', { ascending: true, nullsFirst: false });
      break;
    case 'price_high':
      query = query.order('list_price', { ascending: false, nullsFirst: false });
      break;
    case 'beds_low':
      query = query.order('bedrooms', { ascending: true, nullsFirst: false });
      break;
    case 'beds_high':
      query = query.order('bedrooms', { ascending: false, nullsFirst: false });
      break;
    case 'newest':
    default:
      query = query.order('listing_date', { ascending: false, nullsFirst: false });
      break;
  }

  // Apply pagination (with retry for transient errors)
  let data: any[] | null = null;
  let count: number | null = null;
  let lastError: any = null;

  for (let attempt = 0; attempt < 2; attempt++) {
    const result = await query.range(from, to);
    if (!result.error) {
      data = result.data;
      count = result.count;
      lastError = null;
      break;
    }
    lastError = result.error;
    console.error(`Listings query attempt ${attempt + 1} failed:`, result.error.code, result.error.message);
  }

  if (lastError) {
    return {
      listings: [],
      total: 0,
      page,
      pageSize,
      totalPages: 0,
    };
  }

  const total = count || 0;
  // Belt and braces on the query-level exclusion above: the IN list is
  // case-sensitive, so an oddly-cased 'EXPIRED' from the feed would slip past
  // it. isOffMarketStatus folds case.
  const allListings = (data || [])
    .map(transformListing)
    .filter((l) => !isOffMarketStatus(l.status));
  const listings = deduplicateListings(allListings);
  const dedupedTotal = total - (allListings.length - listings.length);

  return {
    listings,
    total: dedupedTotal > 0 ? dedupedTotal : listings.length,
    page,
    pageSize,
    totalPages: Math.ceil((dedupedTotal > 0 ? dedupedTotal : listings.length) / pageSize),
  };
}

/**
 * Property-type-specific listing fetchers. These bypass the default
 * lease-exclusion filter applied by getListings() and instead query
 * directly for the type the hub page wants. Used by /rentals,
 * /commercial, and /land.
 */
async function getListingsByPropertyTypes(opts: {
  propertyTypes?: string[];
  propertySubTypes?: string[];
  city?: string | null;
  page?: number;
  pageSize?: number;
  excludedStatuses?: string[];
  /** Rentals hub only — keep lease listings in the result. */
  includeLeases?: boolean;
}): Promise<HubListings> {
  if (!isSupabaseConfigured()) return { listings: [], total: 0 };
  const pageSize = opts.pageSize ?? HUB_PAGE_SIZE;
  const page = Math.max(1, opts.page ?? 1);
  let query = supabase
    .from('mls_properties')
    .select('*', { count: 'exact' });
  if (opts.propertyTypes && opts.propertyTypes.length > 0) {
    // Case-insensitive exact match per value (see getListings) — the feed mixes
    // 'RES Vacant Land' and 'Res Vacant Land'. Values are quoted because they
    // contain spaces.
    query = query.or(
      opts.propertyTypes.map((t) => `property_type.ilike."${t}"`).join(',')
    );
  }
  if (opts.propertySubTypes && opts.propertySubTypes.length > 0) {
    query = query.in('property_sub_type', opts.propertySubTypes);
  }
  if (opts.city) {
    query = query.ilike('city', opts.city);
  }
  if (opts.excludedStatuses && opts.excludedStatuses.length > 0) {
    query = query.not('status', 'in', `(${opts.excludedStatuses.join(',')})`);
  }
  // On-market only. These hubs are "<type> for sale" pages, but the query had
  // no status filter, so sold listings padded the grid and the count — /condos
  // read "500 properties" (the query cap) against 487 actually for sale. It
  // also disagreed with the city links, which have always been derived from
  // active inventory.
  query = query.in('status', ACTIVE_STATUSES);
  // Mirror the display-time auto-close rule getListings applies: a row can
  // carry an Active status while its sold_date has already passed, and
  // transformListing labels exactly those Sold.
  query = query.or(`sold_date.is.null,sold_date.gte.${new Date().toISOString()}`);

  // A sub-type hub selects on property_sub_type alone, and 'Condominium' or
  // 'Commercial' describes a lease listing just as well as a sale. Without
  // this, /condos/aspen carried 222 monthly rentals — $7,500 and $12,000
  // "condos" — alongside 172 genuine sales. /rentals opts back in, since
  // selecting lease types is the whole point of that hub.
  if (!opts.includeLeases) {
    query = query.or(
      `property_type.not.in.(${EXCLUDED_LEASE_TYPES.join(',')}),property_type.is.null`
    );
  }
  // Same rule as getListings: a listing pulled from the MLS 404s on its detail
  // page, so it has no business on the /rentals, /land or /commercial hubs.
  const offMarketList = OFF_MARKET_STATUS_VALUES.map((v) => `"${v}"`).join(',');
  query = query.or(`status.is.null,status.not.in.(${offMarketList})`);
  query = query.order('list_price', { ascending: false, nullsFirst: false });

  // Fetch only the page being rendered. This used to pull up to 500 rows and
  // slice in memory, which meant every hub paid for the whole result set to
  // show 48 cards.
  const from = (page - 1) * pageSize;
  const { data, error, count } = await query.range(from, from + pageSize - 1);
  if (error) {
    console.error('Error fetching listings by property type:', error);
    return { listings: [], total: 0 };
  }
  const rows = (data || []).map(transformListing).filter((l) => !isOffMarketStatus(l.status));
  return { listings: deduplicateListings(rows), total: count ?? rows.length };
}

// Lightweight listing shape with just the fields the sitemap needs.
// Returning the full row from getListings() (photos arrays, descriptions,
// agent details, etc.) for all ~5k MLS records makes /sitemap-listings.xml
// regenerate in 17-30s, which exceeds Vercel-edge regeneration windows AND
// crawler timeouts. Selecting only what getListingHref() needs cuts the
// transfer + parsing by ~95%.
export interface SitemapListingRow {
  id: string;
  mls_number: string | null;
  status: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  updated_at: string | null;
}

export async function getListingsForSitemap(): Promise<SitemapListingRow[]> {
  if (!isSupabaseConfigured()) return [];

  // Exactly the inventory the site shows: the allowed towns and property
  // types from MLS Configuration, and active-type statuses. The old version
  // took an unordered 6,000-row slice of the whole 43,000-row feed, which was
  // 96% closed sales in other counties and missed most live listings.
  const { getMLSConfiguration, getAllowedCities, getExcludedPropertyTypes } = await import('./mlsConfiguration');
  const config = await getMLSConfiguration();
  const allowedCities = getAllowedCities(config);
  const excludedTypes = getExcludedPropertyTypes(config);
  const liveStatuses = ['Active', 'Active Under Contract', 'Active U/C W/ Bump', 'Pending', 'Pending Inspect/Feasib', 'To Be Built'];

  const out: SitemapListingRow[] = [];
  const PAGE_SIZE = 1000;
  for (let page = 0; page < 20; page++) {
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    let query = supabase
      .from('mls_properties')
      .select('id, mls_number, status, address, city, state, zip_code, updated_at')
      .eq('is_active', true)
      .not('mls_number', 'is', null)
      .in('status', liveStatuses)
      .order('mls_number', { ascending: true })
      .range(from, to);
    if (allowedCities.length > 0) query = query.in('city', allowedCities);
    if (excludedTypes.length > 0) query = query.not('property_type', 'in', `(${excludedTypes.map((t) => `"${t}"`).join(',')})`);
    const { data, error } = await query;
    if (error) {
      console.error('[getListingsForSitemap] page', page, 'error:', error);
      break;
    }
    if (!data || data.length === 0) break;
    out.push(...(data as SitemapListingRow[]).filter((r) => !isOffMarketStatus(r.status)));
    if (data.length < PAGE_SIZE) break;
  }
  return out;
}

export async function getRentals(city?: string | null, page = 1): Promise<HubListings> {
  return getListingsByPropertyTypes({
    propertyTypes: EXCLUDED_LEASE_TYPES, // 'Residential Lease', 'Commercial Lease'
    city,
    page,
    includeLeases: true,
  });
}

export async function getLandListings(city?: string | null, page = 1): Promise<HubListings> {
  return getListingsByPropertyTypes({
    propertyTypes: LAND_PROPERTY_TYPES,
    city,
    page,
  });
}

export async function getCondoListings(city?: string | null, page = 1): Promise<HubListings> {
  return getListingsByPropertyTypes({
    propertySubTypes: CONDO_SUB_TYPES,
    city,
    page,
  });
}
export async function getCommercialListings(city?: string | null, page = 1): Promise<HubListings> {
  return getListingsByPropertyTypes({
    propertySubTypes: COMMERCIAL_SUB_TYPES,
    city,
    page,
  });
}

// Look up SIR media for a listing by its MLS number
async function getSIRMediaForListing(mlsNumber: string): Promise<SIRMediaAssets | null> {
  if (!isRealogyConfigured()) return null;

  const realogySupabase = getRealogySupabase();
  if (!realogySupabase) return null;

  // Order by id descending so that when duplicate MLS numbers exist across
  // multiple SIR rows, we consistently pick the most recently inserted one.
  const { data, error } = await realogySupabase
    .from('realogy_listings')
    .select('default_photo_url, media, state_province_code')
    .contains('mls_numbers', JSON.stringify([mlsNumber]))
    .order('id', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error(`[SIR Enrichment] Query error for MLS# ${mlsNumber}:`, error.message);
    return null;
  }
  if (!data) return null;

  const media = extractSIRMedia(data);
  media.state = data.state_province_code || null;
  return media;
}

export async function getListingById(id: string): Promise<MLSProperty | null> {
  if (!isSupabaseConfigured()) return null;

  const { data, error } = await supabase
    .from('mls_properties')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching listing:', error);
    return null;
  }

  if (!data) return null;
  const listing = transformListing(data);

  // Enrich with SIR media (better photos, virtual tours, videos)
  // Skip SIR enrichment if the SIR record is for a different state (duplicate MLS number)
  const sirMedia = await getSIRMediaForListing(listing.mls_number);
  const sirMatchesListing = sirMedia && (!sirMedia.state || !listing.state || sirMedia.state === listing.state);
  return sirMatchesListing ? enrichListingWithSIRMedia(listing, sirMedia) : listing;
}

export async function getListingByMlsNumber(mlsNumber: string): Promise<MLSProperty | null> {
  if (!isSupabaseConfigured()) return null;

  // Fetch MLS listing and SIR media in parallel (we already have the MLS number)
  // Use limit(1) instead of single() to handle duplicate listing_id rows gracefully
  const [mlsResult, sirMedia] = await Promise.all([
    supabase
      .from('mls_properties')
      .select('*')
      .eq('mls_number', mlsNumber)
      .not('status', 'is', null)
      .order('updated_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle(),
    getSIRMediaForListing(mlsNumber),
  ]);

  if (mlsResult.error) {
    console.error('Error fetching listing:', mlsResult.error);
    return null;
  }

  if (!mlsResult.data) return null;
  const listing = transformListing(mlsResult.data);

  // Skip SIR enrichment if the SIR record is for a different state (duplicate MLS number)
  const sirMatchesListing = sirMedia && (!sirMedia.state || !listing.state || sirMedia.state === listing.state);
  return sirMatchesListing ? enrichListingWithSIRMedia(listing, sirMedia) : listing;
}

// Look up a listing in the Realogy/SIR database by rfg_listing_id or entity_id
async function getRealogyListingBySlug(slug: string): Promise<MLSProperty | null> {
  if (!isRealogyConfigured()) return null;

  const realogySupabase = getRealogySupabase();
  if (!realogySupabase) return null;

  const { data, error } = await realogySupabase
    .from('realogy_listings')
    .select(`
      id, entity_id, rfg_listing_id, is_active, price_amount, street_address,
      city, state_province_code, postal_code, district, no_of_bedrooms, total_bath,
      full_bath, half_bath, three_quarter_bath, square_footage, building_area,
      lot_size, year_built, property_type, listed_on, default_photo_url, media,
      primary_agent_name, latitude, longitude, created_at, synced_at
    `)
    // Column is rfg_listing_id on realogy_listings (no rfg_mls_number);
    // the older name was a stale rename that crashed the query with
    // 42703 every time a Realogy slug came through.
    .or(`rfg_listing_id.eq.${slug},entity_id.eq.${slug}`)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching Realogy listing:', error);
    return null;
  }

  if (!data) return null;
  return transformRealogyListing(data);
}

// Look up a listing by address slug (e.g. "3000-Ralston-Avenue")
async function getRealogyListingByAddressSlug(slug: string): Promise<MLSProperty | null> {
  if (!isRealogyConfigured()) return null;

  // The slug was produced by toAddressSlug(), which STRIPS punctuation ('#', ',',
  // '.', apostrophes) and collapses whitespace to single hyphens. The stored
  // street_address keeps that punctuation (e.g. "33 S Palm Avenue # 1501"), so
  // simply reversing hyphens to spaces and prefix-matching the raw value misses.
  // Match token-by-token with '%' between tokens so any stripped characters in the
  // stored address are tolerated, then confirm the round-trip in JS by slugifying
  // each candidate the same way the links were built.
  const tokens = decodeURIComponent(slug)
    .split('-')
    .map((t) => t.replace(/[^a-zA-Z0-9]/g, ''))
    .filter(Boolean);
  if (tokens.length === 0) return null;
  const pattern = `${tokens.join('%')}%`;

  const realogySupabase = getRealogySupabase();
  if (!realogySupabase) return null;

  const { data, error } = await realogySupabase
    .from('realogy_listings')
    .select(`
      id, entity_id, rfg_listing_id, is_active, price_amount, street_address,
      city, state_province_code, postal_code, district, no_of_bedrooms, total_bath,
      full_bath, half_bath, three_quarter_bath, square_footage, building_area,
      lot_size, year_built, property_type, listed_on, default_photo_url, media,
      primary_agent_name, latitude, longitude, created_at, synced_at
    `)
    .ilike('street_address', pattern)
    .order('price_amount', { ascending: false })
    .limit(10);

  if (error || !data || data.length === 0) return null;

  // Prefer the row whose address slugifies back to exactly this slug (the same
  // transform the tile/canonical used), so links round-trip 1:1.
  const target = slug.toLowerCase();
  const exact = data.find(
    (row: any) => toAddressSlug(row.street_address).toLowerCase() === target
  );
  const best = exact || data[0];

  return transformRealogyListing(best);
}

// Generate an address slug for a street address (e.g. "3000 Ralston Avenue" -> "3000-Ralston-Avenue")
export function toAddressSlug(streetAddress: string | null | undefined): string {
  if (!streetAddress) return '';
  return streetAddress
    .trim()
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '-');
}

export async function getOpenHouseListings(): Promise<MLSProperty[]> {
  if (!isSupabaseConfigured()) {
    console.error('[OpenHouse] Supabase not configured');
    return [];
  }

  const today = new Date().toISOString().split('T')[0];
  console.log('[OpenHouse] Fetching open houses for date >=', today);

  // Open house data comes from two sources:
  //   1. SparkAPI "open_houses" table (synced from RESO OpenHouse endpoint)
  //   2. "rc-listings" base table (has OpenHouseDate columns for all brokerages,
  //      including Retter & Co / Sotheby's that aren't in SparkAPI)
  // We query both and merge, deduplicating by listing ID + date.

  type OpenHouseRecord = {
    ListingId: string;
    OpenHouseDate: string;
    OpenHouseStartTime: string | null;
    OpenHouseEndTime: string | null;
    OpenHouseRemarks: string | null;
  };

  // Step 1a: Get open houses from SparkAPI table
  const { data: sparkData, error: sparkError } = await supabase
    .from('open_houses')
    .select('"ListingId", "OpenHouseDate", "OpenHouseStartTime", "OpenHouseEndTime", "OpenHouseRemarks"')
    .gte('OpenHouseDate', today)
    .order('OpenHouseDate', { ascending: true })
    .limit(500);

  if (sparkError) {
    console.error('[OpenHouse] SparkAPI query error:', sparkError.message);
  }
  console.log('[OpenHouse] SparkAPI:', sparkData?.length || 0, 'records');

  // Step 1b: Get open houses from rc-listings (requires service role for performance)
  let rcData: OpenHouseRecord[] = [];
  const serverClient = getSupabaseServer();
  if (serverClient) {
    const { data, error: rcError } = await serverClient
      .from('rc-listings')
      .select('"ListingId", "OpenHouseDate", "OpenHouseStartTime", "OpenHouseEndTime", "OpenHouseRemarks"')
      .gte('OpenHouseDate', today)
      .order('OpenHouseDate', { ascending: true })
      .limit(500);

    if (rcError) {
      console.error('[OpenHouse] rc-listings query error:', rcError.message);
    } else {
      rcData = (data || []) as OpenHouseRecord[];
    }
    console.log('[OpenHouse] rc-listings:', rcData.length, 'records');
  }

  // Step 1c: Merge and deduplicate by ListingId + OpenHouseDate
  const seen = new Set<string>();
  const allOpenHouses: OpenHouseRecord[] = [];

  // rc-listings is the more comprehensive source — add it first
  for (const oh of rcData) {
    if (!oh.ListingId || !oh.OpenHouseDate) continue;
    const key = `${oh.ListingId}|${oh.OpenHouseDate}`;
    if (!seen.has(key)) {
      seen.add(key);
      allOpenHouses.push(oh);
    }
  }
  // Then add any SparkAPI records not already seen
  for (const oh of (sparkData || []) as OpenHouseRecord[]) {
    if (!oh.ListingId || !oh.OpenHouseDate) continue;
    const key = `${oh.ListingId}|${oh.OpenHouseDate}`;
    if (!seen.has(key)) {
      seen.add(key);
      allOpenHouses.push(oh);
    }
  }

  if (allOpenHouses.length === 0) {
    console.error('[OpenHouse] No open house records found from any source');
    return [];
  }
  console.log('[OpenHouse] Merged:', allOpenHouses.length, 'unique open house records');

  // Step 2: Get unique listing IDs and fetch full listing data
  const listingIds = [...new Set(allOpenHouses.map((oh) => oh.ListingId))];
  console.log('[OpenHouse] Looking up', listingIds.length, 'listings');

  const { data: listings, error: listError } = await supabase
    .from('mls_properties')
    .select('*')
    .in('mls_number', listingIds)
    .not('status', 'is', null);

  if (listError) {
    console.error('[OpenHouse] Listings lookup error:', listError.message, listError);
    return [];
  }
  if (!listings || listings.length === 0) {
    console.error('[OpenHouse] No matching listings found');
    return [];
  }
  console.log('[OpenHouse] Matched', listings.length, 'listings');

  // Step 3: Merge open house data onto listings (prefer rows with address data)
  const listingMap = new Map<string, any>();
  for (const l of listings) {
    const key = l.mls_number || l.listing_id;
    const existing = listingMap.get(key);
    if (!existing || (l.address && !existing.address)) {
      listingMap.set(key, l);
    }
  }

  const results: MLSProperty[] = [];
  for (const oh of allOpenHouses) {
    const listing = listingMap.get(oh.ListingId);
    if (!listing) continue;
    // Skip lease/rental properties
    if (EXCLUDED_LEASE_TYPES.includes(listing.property_type)) continue;

    const merged = transformListing({
      ...listing,
      open_house_date: oh.OpenHouseDate,
      open_house_start_time: oh.OpenHouseStartTime,
      open_house_end_time: oh.OpenHouseEndTime,
      open_house_remarks: oh.OpenHouseRemarks,
    });
    results.push(merged);
  }

  return results;
}

// Cities are derived from the ACTIVE inventory, not from every row in the
// table. The previous implementation scanned the first 5,000 rows ordered by
// city, which — with 21k sold listings and thousands in Aspen alone — ran out
// inside the A's: the list was Alamosa, Antonito, Arvada, Ashland, Aspen, and
// every downvalley market was missing. That 404'd /land/basalt and
// /rentals/carbondale while publishing empty hubs for towns 200 miles away.
//
// Filtering to active statuses keeps the scan comfortably inside one window
// AND drops towns that have nothing for sale, so a hub page never exists for a
// place with no inventory.
const ACTIVE_STATUSES = [
  'Active',
  'Active Under Contract',
  'Active U/C W/ Bump',
  'Pending',
  'Pending Inspect/Feasib',
  'To Be Built',
];

interface ActiveCityRow {
  city: string;
  property_type: string | null;
  property_sub_type: string | null;
}

// One cached scan feeds every city list below.
function getActiveCityRows(): Promise<ActiveCityRow[]> {
  return getCached('activeCityRows', 5 * 60 * 1000, async () => {
    if (!isSupabaseConfigured()) return [];
    const batchSize = 1000;
    const numBatches = 6;
    const batches = await Promise.all(
      Array.from({ length: numBatches }, (_, i) =>
        supabase
          .from('mls_properties')
          .select('city, property_type, property_sub_type')
          .not('city', 'is', null)
          .in('status', ACTIVE_STATUSES)
          .order('city')
          .range(i * batchSize, (i + 1) * batchSize - 1)
      )
    );
    const rows: ActiveCityRow[] = [];
    for (const { data, error } of batches) {
      if (error) {
        console.error('Error fetching active cities:', error.message);
        continue;
      }
      for (const r of (data || []) as ActiveCityRow[]) {
        if (r.city) rows.push(r);
      }
    }
    return rows;
  });
}

export function getDistinctCities(): Promise<string[]> {
  return getActiveCityRows().then((rows) =>
    [...new Set(rows.map((r) => r.city))].sort()
  );
}

// Cities that actually hold inventory of a given kind. Hub pages resolve their
// city segment through these, so /land/<city> only exists where there is land
// to show — and the city links on a hub never point at a page that 404s.
function citiesMatching(
  match: (row: ActiveCityRow) => boolean
): Promise<string[]> {
  return getActiveCityRows().then((rows) =>
    [...new Set(rows.filter(match).map((r) => r.city))].sort()
  );
}

const eqAny = (value: string | null, options: string[]) =>
  !!value && options.some((o) => o.toLowerCase() === value.trim().toLowerCase());

export function getRentalCities(): Promise<string[]> {
  return citiesMatching((r) => eqAny(r.property_type, EXCLUDED_LEASE_TYPES));
}

export function getLandCities(): Promise<string[]> {
  return citiesMatching((r) => eqAny(r.property_type, LAND_PROPERTY_TYPES));
}

export function getCommercialCities(): Promise<string[]> {
  return citiesMatching((r) => eqAny(r.property_sub_type, COMMERCIAL_SUB_TYPES));
}

export function getCondoCities(): Promise<string[]> {
  return citiesMatching((r) => eqAny(r.property_sub_type, CONDO_SUB_TYPES));
}

// Main property types from database (property_type column).
// Hardcoded to avoid Supabase 1000 row limit issues. These must match the Aspen
// Glenwood MLS vocabulary in sanity/schemaTypes/mlsConfiguration.ts — earlier
// values here ('Land', 'Farm') were carried over from a different MLS and matched
// nothing, so selecting them returned an empty result set.
const PROPERTY_TYPES = [
  'Commercial Land',
  'Commercial Sale',
  'Fractional',
  'RES Vacant Land',
  'Residential',
];

// Property subtypes from database (property_sub_type column)
const PROPERTY_SUB_TYPES = [
  'Agricultural',
  'Agriculture',
  'Business with Real Estate',
  'Commercial',
  'Commercial Land',
  'Condominium',
  'Development',
  'Duplex',
  'Half Duplex',
  'Mobile Home',
  'Multi-Family Lot',
  'Residential Income',
  'Seasonal & Remote',
  'Single Family Lot',
  'Single Family Residence',
  'Townhouse',
];

export async function getDistinctPropertyTypes(): Promise<string[]> {
  // Return hardcoded list to avoid Supabase 1000 row limit issues
  return PROPERTY_TYPES;
}

export async function getDistinctPropertySubTypes(): Promise<string[]> {
  // Return hardcoded list to avoid Supabase 1000 row limit issues
  return PROPERTY_SUB_TYPES;
}

// Statuses from database — hardcoded to avoid multiple Supabase batch queries
const STATUSES = [
  'Active',
  'Active Under Contract',
  'Active U/C W/ Bump',
  'Closed',
  'Coming Soon',
  'Contingent',
  'Pending',
  'Pending Inspect/Feasib',
  'Sold',
  'To Be Built',
  'Withdrawn',
];

// Statuses that mean a listing is permanently off the market and should not be
// browsable anywhere on the site — it was pulled from the MLS and is no longer
// for sale. This is distinct from Sold/Closed, which intentionally keep a page
// (the sold gallery).
//
// Spelled as the feed stores them so they can be handed straight to a query;
// isOffMarketStatus() matches case-insensitively for display-time checks.
export const OFF_MARKET_STATUS_VALUES = [
  'Expired',
  'Withdrawn',
  'Canceled',
  'Cancelled',
  'Deleted',
  'Off Market',
  'Off-Market',
];

const OFF_MARKET_STATUSES = new Set(
  OFF_MARKET_STATUS_VALUES.map((s) => s.toLowerCase())
);

export function isOffMarketStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return OFF_MARKET_STATUSES.has(status.trim().toLowerCase());
}

export async function getDistinctStatuses(): Promise<string[]> {
  return STATUSES;
}

/**
 * Find any mls_area_minor values whose name contains the given community
 * name (case-insensitive). Used as a fallback on community pages when no
 * mlsAreaMinors are explicitly set in Sanity — "Red Mountain" → ["01-Red Mountain"],
 * "Snowmass Village" → ["02-Snowmass Village"], etc. Editors can still
 * override by picking from the explicit dropdown.
 */
export function findMlsAreaMinorsByName(name: string): Promise<string[]> {
  const trimmed = (name || '').trim();
  if (!trimmed) return Promise.resolve([]);
  return getCached(`mlsAreaMinorsByName:${trimmed.toLowerCase()}`, 60 * 60 * 1000, async () => {
    if (!isSupabaseConfigured()) return [];
    const { data, error } = await supabase
      .from('mls_properties')
      .select('mls_area_minor')
      .not('mls_area_minor', 'is', null)
      .ilike('mls_area_minor', `%${trimmed}%`)
      .limit(1000);
    if (error) {
      console.error('[findMlsAreaMinorsByName] query failed:', error.message);
      return [];
    }
    const unique = Array.from(
      new Set((data || []).map((r) => r.mls_area_minor as string).filter(Boolean))
    );
    return unique.sort();
  });
}

export function getDistinctNeighborhoods(): Promise<string[]> {
  return getCached('distinctNeighborhoods', 5 * 60 * 1000, async () => {
    if (!isSupabaseConfigured()) return [];

    // Sample rows to extract distinct neighborhoods (cap at 5 batches to avoid rate limits)
    const allNeighborhoods = new Set<string>();
    let offset = 0;
    const batchSize = 1000;
    const maxBatches = 5;

    for (let batch = 0; batch < maxBatches; batch++) {
      const { data, error } = await supabase
        .from('mls_properties')
        .select('subdivision_name')
        .not('subdivision_name', 'is', null)
        .order('subdivision_name')
        .range(offset, offset + batchSize - 1);

      if (error) {
        console.error('Error fetching neighborhoods:', error);
        break;
      }
      if (!data || data.length === 0) break;

      data.forEach((d) => { if (d.subdivision_name) allNeighborhoods.add(d.subdivision_name); });
      if (data.length < batchSize) break;
      offset += batchSize;
    }

    return [...allNeighborhoods].sort();
  });
}

export function getNeighborhoodsByCity(city: string): Promise<string[]> {
  return getCached(`neighborhoodsByCity:${city}`, 5 * 60 * 1000, async () => {
    if (!isSupabaseConfigured()) return [];

    // Cap at 5 batches to avoid rate limits on large datasets
    const allNeighborhoods = new Set<string>();
    let offset = 0;
    const batchSize = 1000;
    const maxBatches = 5;

    for (let batch = 0; batch < maxBatches; batch++) {
      const { data, error } = await supabase
        .from('mls_properties')
        .select('subdivision_name')
        .ilike('city', city)
        .not('subdivision_name', 'is', null)
        .order('subdivision_name')
        .range(offset, offset + batchSize - 1);

      if (error) {
        console.error('Error fetching neighborhoods for city:', error);
        break;
      }
      if (!data || data.length === 0) break;

      data.forEach((d) => { if (d.subdivision_name) allNeighborhoods.add(d.subdivision_name); });
      if (data.length < batchSize) break;
      offset += batchSize;
    }

    return [...allNeighborhoods].sort();
  });
}

export function getNeighborhoodsByCities(cities: string[]): Promise<string[]> {
  const cacheKey = `neighborhoodsByCities:${[...cities].sort().join(',')}`;
  return getCached(cacheKey, 5 * 60 * 1000, async () => {
    if (!isSupabaseConfigured() || cities.length === 0) return [];

    const allNeighborhoods = new Set<string>();
    let offset = 0;
    const batchSize = 1000;
    const maxBatches = 5;

    for (let batch = 0; batch < maxBatches; batch++) {
      const { data, error } = await supabase
        .from('mls_properties')
        .select('subdivision_name')
        .in('city', cities)
        .not('subdivision_name', 'is', null)
        .order('subdivision_name')
        .range(offset, offset + batchSize - 1);

      if (error) {
        console.error('Error fetching neighborhoods for cities:', error);
        break;
      }
      if (!data || data.length === 0) break;

      data.forEach((d) => { if (d.subdivision_name) allNeighborhoods.add(d.subdivision_name); });
      if (data.length < batchSize) break;
      offset += batchSize;
    }

    return [...allNeighborhoods].sort();
  });
}

/**
 * The feed sometimes carries one property under two MLS numbers (a relist,
 * or a rental and a sale record): two pages with the same title, the same
 * remarks and the same price. The older page canonicalises to the newer
 * one instead of the two competing as duplicates.
 */
export async function findNewerDuplicateListing(
  listing: Pick<MLSProperty, 'id' | 'mls_number' | 'address' | 'list_price'>
): Promise<MLSProperty | null> {
  if (!isSupabaseConfigured() || !listing.address || !listing.list_price) return null;
  const { data, error } = await supabase
    .from('mls_properties')
    .select('*')
    .eq('address', listing.address)
    .eq('list_price', listing.list_price)
    .neq('id', listing.id)
    .not('status', 'is', null)
    .order('mls_number', { ascending: false })
    .limit(1);
  if (error || !data || data.length === 0) return null;
  const other = transformListing(data[0] as GraphQLListing);
  if (isOffMarketStatus(other.status)) return null;
  const mine = Number(listing.mls_number) || 0;
  const theirs = Number(other.mls_number) || 0;
  return theirs > mine ? other : null;
}

export function formatPrice(price: number | null): string {
  if (!price) return 'Price N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

export function formatSqft(sqft: number | null): string {
  if (!sqft) return '';
  return new Intl.NumberFormat('en-US').format(sqft) + ' sq ft';
}

export function formatLotSize(acres: number | null): string {
  if (!acres) return '';
  if (acres >= 1) {
    return acres.toFixed(2) + ' acres';
  }
  // Convert to sq ft for smaller lots
  const sqft = acres * 43560;
  return new Intl.NumberFormat('en-US').format(Math.round(sqft)) + ' sq ft lot';
}

// Get newest highest-priced properties for a specific city
export async function getNewestHighPricedByCity(
  city: string,
  limit: number = 4,
  options?: { agentIds?: string[]; officeName?: string; minPrice?: number; sortBy?: 'date' | 'price' }
): Promise<MLSProperty[]> {
  if (!isSupabaseConfigured()) return [];

  // When filtering by officeName, skip the DB filter (no index on list_office_name causes timeouts)
  // Instead, overfetch and filter in JS
  const overfetchMultiplier = options?.officeName ? 5 : 1;

  // Use graphql_listings materialized view (indexed, ~3K rows) instead of
  // graphql_listings view (100K+ rows, COALESCE prevents index usage, causes timeouts)
  let query = supabase
    .from('mls_properties')
    .select('*')
    .ilike('city', city)
    .eq('property_type', 'Residential')
    .not('list_price', 'is', null);

  if (options?.minPrice) {
    query = query.gte('list_price', options.minPrice);
  }

  if (!options?.officeName && options?.agentIds && options.agentIds.length > 0) {
    const agentFilter = options.agentIds.map(id => `list_agent_mls_id.ilike.${id}`).join(',');
    query = query.or(agentFilter);
  }

  if (options?.sortBy === 'price') {
    query = query.order('list_price', { ascending: false });
  } else {
    query = query
      .order('listing_date', { ascending: false })
      .order('list_price', { ascending: false });
  }

  const { data, error } = await query.limit(limit * overfetchMultiplier);

  if (error) {
    console.error('Error fetching newest high-priced listings:', error);
    return [];
  }

  let rows = data || [];

  // Filter by office name in JS to avoid query timeout
  if (options?.officeName) {
    const officeNameLower = options.officeName.toLowerCase();
    rows = rows.filter(row => row.list_office_name?.toLowerCase().includes(officeNameLower));
    rows = rows.slice(0, limit);
  }

  const listings = deduplicateListings(rows.map(transformListing));
  return enrichListingsWithSIRMedia(listings);
}

/**
 * Get price range for a city: lowest priced condo and highest priced single family home
 */
export async function getCommunityPriceRange(
  city: string
): Promise<{ lowestCondo: number | null; highestSingleFamily: number | null }> {
  if (!isSupabaseConfigured()) return { lowestCondo: null, highestSingleFamily: null };

  // Use graphql_listings materialized view (indexed, fast)
  const { data: condoData, error: condoError } = await supabase
    .from('mls_properties')
    .select('list_price')
    .ilike('city', city)
    .or('property_sub_type.eq.Condominium,property_sub_type.is.null')
    .eq('status', 'Active')
    .not('list_price', 'is', null)
    .order('list_price', { ascending: true })
    .limit(1);

  if (condoError) {
    console.error('Error fetching lowest condo price:', condoError);
  }

  // Get highest priced active listing (prefer SFR, fall back to any type)
  const { data: sfhData, error: sfhError } = await supabase
    .from('mls_properties')
    .select('list_price')
    .ilike('city', city)
    .or('property_sub_type.eq.Single Family Residence,property_sub_type.eq.Site Built-Owned Lot,property_sub_type.is.null')
    .eq('status', 'Active')
    .not('list_price', 'is', null)
    .order('list_price', { ascending: false })
    .limit(1);

  if (sfhError) {
    console.error('Error fetching highest single family price:', sfhError);
  }

  return {
    lowestCondo: condoData?.[0]?.list_price ?? null,
    highestSingleFamily: sfhData?.[0]?.list_price ?? null,
  };
}

/**
 * Fetches newest high-priced Single Family Residential properties from multiple cities
 */
export async function getNewestHighPricedByCities(
  cities: string[],
  limit: number = 8,
  options?: { agentIds?: string[]; officeName?: string; minPrice?: number; sortBy?: 'date' | 'price' }
): Promise<MLSProperty[]> {
  if (!isSupabaseConfigured() || !cities || cities.length === 0) {
    return [];
  }

  // Build OR filter for multiple cities (case-insensitive)
  const cityFilters = cities.map(city => `city.ilike.${city}`).join(',');

  // When filtering by officeName, skip the DB filter (no index on list_office_name causes timeouts)
  // Instead, overfetch and filter in JS
  const overfetchMultiplier = options?.officeName ? 5 : 1;

  // Use graphql_listings materialized view (indexed, ~3K rows) instead of
  // graphql_listings view (100K+ rows, COALESCE prevents index usage, causes timeouts)
  let query = supabase
    .from('mls_properties')
    .select('*')
    .or(cityFilters)
    .eq('property_type', 'Residential')
    .not('list_price', 'is', null);

  if (options?.minPrice) {
    query = query.gte('list_price', options.minPrice);
  }

  if (!options?.officeName && options?.agentIds && options.agentIds.length > 0) {
    const agentFilter = options.agentIds.map(id => `list_agent_mls_id.ilike.${id}`).join(',');
    query = query.or(agentFilter);
  }

  if (options?.sortBy === 'price') {
    query = query.order('list_price', { ascending: false });
  } else {
    query = query
      .order('listing_date', { ascending: false })
      .order('list_price', { ascending: false });
  }

  const { data, error } = await query.limit(limit * overfetchMultiplier);

  if (error) {
    console.error('Error fetching newest high-priced listings by cities:', error);
    return [];
  }

  let rows = data || [];

  // Filter by office name in JS to avoid query timeout
  if (options?.officeName) {
    const officeNameLower = options.officeName.toLowerCase();
    rows = rows.filter(row => row.list_office_name?.toLowerCase().includes(officeNameLower));
    rows = rows.slice(0, limit);
  }

  const listings = deduplicateListings(rows.map(transformListing));
  return enrichListingsWithSIRMedia(listings);
}

// Enrich an array of listings with SIR media in parallel
async function enrichListingsWithSIRMedia(listings: MLSProperty[]): Promise<MLSProperty[]> {
  if (!isRealogyConfigured() || listings.length === 0) return listings;

  return Promise.all(
    listings.map(async (listing) => {
      if (!listing.mls_number) return listing;
      const sirMedia = await getSIRMediaForListing(listing.mls_number);
      const sirMatchesListing = sirMedia && (!sirMedia.state || !listing.state || sirMedia.state === listing.state);
      return sirMatchesListing ? enrichListingWithSIRMedia(listing, sirMedia) : listing;
    })
  );
}

// Extract and categorize media from a Realogy/SIR listing row
interface SIRMediaAssets {
  photos: string[];
  videoUrls: string[];
  virtualTourUrl: string | null;
  state?: string | null;
}

function extractSIRMedia(row: any): SIRMediaAssets {
  const photos: string[] = [];
  const videoUrls: string[] = [];
  let virtualTourUrl: string | null = null;

  if (row.default_photo_url) {
    const url = row.default_photo_url.startsWith('//')
      ? `https:${row.default_photo_url}`
      : row.default_photo_url;
    photos.push(url);
  }

  if (row.media && Array.isArray(row.media)) {
    for (const item of row.media) {
      let url = item?.url;
      if (!url) continue;
      if (url.startsWith('//')) url = `https:${url}`;

      if (item.format === '3D Video') {
        if (!virtualTourUrl) virtualTourUrl = url;
      } else if (item.format === 'Video') {
        videoUrls.push(url);
      } else {
        if (!photos.includes(url)) photos.push(url);
      }
    }
  }

  return { photos, videoUrls, virtualTourUrl };
}

function enrichListingWithSIRMedia(listing: MLSProperty, sir: SIRMediaAssets): MLSProperty {
  return {
    ...listing,
    photos: sir.photos.length > 0 ? sir.photos : listing.photos,
    virtual_tour_url: sir.virtualTourUrl || listing.virtual_tour_url,
    video_urls: sir.videoUrls.length > 0 ? sir.videoUrls : listing.video_urls,
  };
}

/**
 * Bulk check which MLS numbers have videos in the SIR/Realogy data.
 * Returns a Set of MLS numbers that have at least one video.
 */
export async function getMlsNumbersWithSIRVideos(mlsNumbers: string[]): Promise<Set<string>> {
  const { videos } = await getMlsNumbersWithSIRMedia(mlsNumbers);
  return videos;
}

/**
 * Bulk check which MLS numbers have Matterport/virtual tours in the SIR/Realogy data.
 * Returns a Set of MLS numbers that have at least one 3D Video (Matterport).
 */
export async function getMlsNumbersWithMatterport(mlsNumbers: string[]): Promise<Set<string>> {
  const { matterports } = await getMlsNumbersWithSIRMedia(mlsNumbers);
  return matterports;
}

/**
 * Bulk check which MLS numbers have videos and/or Matterport tours in SIR data.
 * Single query returns both sets to avoid duplicate fetches.
 */
export async function getMlsNumbersWithSIRMedia(mlsNumbers: string[]): Promise<{ videos: Set<string>; matterports: Set<string> }> {
  const empty = { videos: new Set<string>(), matterports: new Set<string>() };
  if (!isRealogyConfigured() || mlsNumbers.length === 0) return empty;

  const realogySupabase = getRealogySupabase();
  if (!realogySupabase) return empty;

  // Query realogy_listings for rows matching any of the MLS numbers
  const { data, error } = await realogySupabase
    .from('realogy_listings')
    .select('mls_numbers, media')
    .or(mlsNumbers.map(n => `mls_numbers.cs.[${JSON.stringify(n)}]`).join(','));

  if (error || !data) return empty;

  const videos = new Set<string>();
  const matterports = new Set<string>();
  for (const row of data) {
    if (row.media && Array.isArray(row.media)) {
      const hasVideo = row.media.some((item: any) => item?.format === 'Video' && item?.url);
      const hasMatterport = row.media.some((item: any) => item?.format === '3D Video' && item?.url);
      if ((hasVideo || hasMatterport) && Array.isArray(row.mls_numbers)) {
        for (const mls of row.mls_numbers) {
          if (mlsNumbers.includes(mls)) {
            if (hasVideo) videos.add(mls);
            if (hasMatterport) matterports.add(mls);
          }
        }
      }
    }
  }

  return { videos, matterports };
}

// Transform a Realogy/SIR listing to the MLSProperty format
// Pretty-print a phone number from the Realogy `agents[0].office.phoneNumber`
// field. Inputs vary: "+19709258810", "+919810823600", "9709258810",
// "(970) 925-8810". US numbers normalize to "(NNN) NNN-NNNN"; non-US fall
// back to the raw string with whitespace trimmed.
function formatRealogyPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return trimmed;
}

function transformRealogyListing(row: any): MLSProperty {
  const sir = extractSIRMedia(row);

  let daysOnMarket: number | null = null;
  if (row.listed_on) {
    const listDate = new Date(row.listed_on);
    daysOnMarket = Math.floor((Date.now() - listDate.getTime()) / (1000 * 60 * 60 * 24));
  }

  // Parse lot_size string like "0.27 AC" to acres number
  let lotSize: number | null = null;
  if (row.lot_size) {
    const match = String(row.lot_size).match(/([\d.]+)/);
    if (match) lotSize = parseFloat(match[1]);
  }

  return {
    id: row.id,
    mls_number: row.rfg_listing_id || row.entity_id || row.id,
    status: row.is_active ? 'Active' : 'Closed',
    list_price: row.price_amount,
    sold_price: row.is_active ? null : row.price_amount,
    address: row.street_address,
    city: row.city,
    state: row.state_province_code,
    zip_code: row.postal_code,
    neighborhood: row.district || null,
    bedrooms: row.no_of_bedrooms,
    bathrooms: row.total_bath,
    bathrooms_full: row.full_bath,
    bathrooms_half: row.half_bath,
    bathrooms_three_quarter: row.three_quarter_bath,
    square_feet: row.square_footage || row.building_area,
    lot_size: lotSize,
    year_built: row.year_built,
    property_type: row.property_type,
    is_rental: isRentalPropertyType(row.property_type),
    listing_date: row.listed_on,
    sold_date: null,
    days_on_market: daysOnMarket,
    description: parseListingRemarks(row.remarks) || row.description || null,
    features: {},
    agent_name: row.primary_agent_name,
    agent_email: null,
    photos: sir.photos,
    video_urls: sir.videoUrls,
    latitude: row.latitude,
    longitude: row.longitude,
    subdivision_name: null,
    mls_area_minor: null,
    furnished: null,
    fireplace_yn: null,
    fireplace_features: null,
    fireplace_total: null,
    cooling: null,
    heating: null,
    laundry_features: null,
    attached_garage_yn: null,
    parking_features: null,
    association_amenities: null,
    virtual_tour_url: sir.virtualTourUrl,
    list_agent_mls_id: null,
    list_agent_full_name: row.primary_agent_name || null,
    co_list_agent_mls_id: null,
    buyer_agent_mls_id: null,
    co_buyer_agent_mls_id: null,
    list_office_name: row.primary_office_name || null,
    list_office_phone: formatRealogyPhone(row.agents?.[0]?.office?.phoneNumber) || null,
    open_house_date: null,
    open_house_start_time: null,
    open_house_end_time: null,
    open_house_remarks: null,
    created_at: row.created_at || row.synced_at || '',
    updated_at: row.synced_at || '',
    is_realogy: true,
  };
}

export async function getRealogyListingsByAgentName(agentName: string): Promise<AgentListingsResult> {
  if (!isRealogyConfigured()) return { activeListings: [], soldListings: [] };

  const realogySupabase = getRealogySupabase();
  if (!realogySupabase) return { activeListings: [], soldListings: [] };

  // Split name into first/last and use wildcards to handle middle names/initials
  // e.g. "Dusty Baker" matches "Dusty J. Baker"
  const nameParts = agentName.trim().split(/\s+/);
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];
  const namePattern = `${firstName}%${lastName}`;

  const { data, error } = await realogySupabase
    .from('realogy_listings')
    .select(`
      id, entity_id, rfg_listing_id, is_active, price_amount, street_address,
      city, state_province_code, postal_code, district, no_of_bedrooms, total_bath,
      full_bath, half_bath, three_quarter_bath, square_footage, building_area,
      lot_size, year_built, property_type, listed_on, default_photo_url, media,
      primary_agent_name, latitude, longitude, created_at, synced_at
    `)
    .ilike('primary_agent_name', namePattern)
    .eq('listing_type', 'ForSale')
    .order('listed_on', { ascending: false })
    .limit(200);

  if (error) {
    console.error('Error fetching Realogy agent listings:', error);
    return { activeListings: [], soldListings: [] };
  }

  const listings = (data || []).map(transformRealogyListing);

  return {
    activeListings: listings.filter((l) => l.status === 'Active'),
    soldListings: listings.filter((l) => l.status !== 'Active'),
  };
}

// Get listings by agent MLS ID, separated into active and sold
export interface AgentListingsResult {
  activeListings: MLSProperty[];
  soldListings: MLSProperty[];
}

export async function getListingsByAgentId(
  agentMlsId: string | null,
  soldAgentMlsId?: string,
  agentName?: string
): Promise<AgentListingsResult> {
  if (!agentMlsId && !agentName) return { activeListings: [], soldListings: [] };

  // Query both sources in parallel and combine results
  const [mlsResult, realogyResult] = await Promise.all([
    // MLS data — only if configured and agent has an MLS ID or name
    (isSupabaseConfigured() && (agentMlsId || agentName))
      ? (async () => {
          // Normalize address for dedup: strip directions, street types, extra whitespace
          const normalizeAddr = (addr: string) =>
            addr
              .toLowerCase()
              .replace(/\b(street|st|avenue|ave|drive|dr|road|rd|way|blvd|boulevard|lane|ln|court|ct|circle|cir|place|pl)\b/g, '')
              .replace(/\b(north|south|east|west|n|s|e|w|ne|nw|se|sw)\b/g, '')
              .replace(/[.,#]/g, '')
              .replace(/\s+/g, ' ')
              .trim();

          const excludeLeases = (listings: any[]) =>
            listings.filter((r) => !EXCLUDED_LEASE_TYPES.includes(r.property_type));

          const dedup = (listings: any[]) => {
            const byListingId = new Map<string, any>();
            const seenByAddress = new Set<string>();
            const result: any[] = [];
            // First pass: for each listing_id, keep the row with more data
            for (const row of listings) {
              if ((row.mls_number || row.listing_id)) {
                const lid = String((row.mls_number || row.listing_id));
                const existing = byListingId.get(lid);
                if (!existing) {
                  byListingId.set(lid, row);
                } else {
                  // Prefer rows with updated_at (stale rows have null), then by data completeness
                  const existingScore = (existing.updated_at ? 100 : 0) + (existing.address ? 1 : 0) + (existing.media ? 1 : 0);
                  const newScore = (row.updated_at ? 100 : 0) + (row.address ? 1 : 0) + (row.media ? 1 : 0);
                  if (newScore > existingScore) {
                    byListingId.set(lid, row);
                  }
                }
              }
            }
            // Second pass: collect deduped rows, also dedup by normalized address
            const emitted = new Set<string>();
            for (const row of listings) {
              if ((row.mls_number || row.listing_id)) {
                const lid = String((row.mls_number || row.listing_id));
                if (emitted.has(lid)) continue;
                const best = byListingId.get(lid) || row;
                emitted.add(lid);
                if (best.address) {
                  const addr = normalizeAddr(String(best.address));
                  if (seenByAddress.has(addr)) continue;
                  seenByAddress.add(addr);
                }
                result.push(best);
              } else {
                if (row.address) {
                  const addr = normalizeAddr(String(row.address));
                  if (seenByAddress.has(addr)) continue;
                  seenByAddress.add(addr);
                }
                result.push(row);
              }
            }
            return result;
          };

          if (agentMlsId) {
            // Query by MLS ID — matches listing and co-listing agent roles
            // Canonical RESO column names now that mls_properties has
            // the full schema. Note: mls_properties does NOT have a
            // co_buyer_agent_mls_id column (only buyer_agent_mls_id),
            // so the old all-roles filter is one role shorter than the
            // graphql_listings version was.
            const buildListingFilter = (id: string) =>
              `list_agent_mls_id.eq.${id},co_list_agent_mls_id.eq.${id}`;
            const buildAllRolesFilter = (id: string) =>
              `list_agent_mls_id.eq.${id},co_list_agent_mls_id.eq.${id},buyer_agent_mls_id.eq.${id}`;

            const activeFilter = buildListingFilter(agentMlsId);
            const soldId = soldAgentMlsId || agentMlsId;
            const soldFilter = soldId === agentMlsId
              ? buildAllRolesFilter(agentMlsId)
              : `${buildAllRolesFilter(agentMlsId)},${buildAllRolesFilter(soldId)}`;

            const activeStatuses = ['Active', 'Coming Soon', 'Active Under Contract', 'Contingent', 'Pending', 'Pending Inspect/Feasib', 'Active U/C W/ Bump', 'To Be Built'];

            const [activeRes, soldRes] = await Promise.all([
              supabase
                .from('mls_properties')
                .select('*')
                .not('mls_number', 'is', null)
                .or(activeFilter)
                .in('status', activeStatuses)
                .order('list_price', { ascending: false })
                .limit(200),
              supabase
                .from('mls_properties')
                .select('*')
                .not('mls_number', 'is', null)
                .or(soldFilter)
                .in('status', ['Closed', 'Sold'])
                .order('sold_price', { ascending: false, nullsFirst: false })
                .limit(200),
            ]);

            if (activeRes.error) console.error('Error fetching active MLS listings:', activeRes.error);
            if (soldRes.error) console.error('Error fetching sold MLS listings:', soldRes.error);

            const activeData = activeRes.data || [];
            const soldData = soldRes.data || [];

            // If MLS ID returned no results but we have an agent name,
            // fall back to name-based query (handles cases where Sanity has
            // a non-numeric ID like a username instead of the real MLS ID)
            if (activeData.length === 0 && soldData.length === 0 && agentName) {
              console.log(`No listings found for MLS ID "${agentMlsId}", falling back to name "${agentName}"`);
              const nameFilter = `list_agent_full_name.ilike.%${agentName}%`;
              const [activeByName, soldByName] = await Promise.all([
                supabase
                  .from('mls_properties')
                  .select('*')
                  .not('mls_number', 'is', null)
                  .or(nameFilter)
                  .in('status', activeStatuses)
                  .order('list_price', { ascending: false })
                  .limit(200),
                supabase
                  .from('mls_properties')
                  .select('*')
                  .not('mls_number', 'is', null)
                  .or(nameFilter)
                  .in('status', ['Closed', 'Sold'])
                  .order('sold_price', { ascending: false, nullsFirst: false })
                  .limit(200),
              ]);
              return {
                activeListings: dedup(excludeLeases(activeByName.data || [])).map(transformListing),
                soldListings: dedup(excludeLeases(soldByName.data || [])).map(transformListing),
              };
            }

            return {
              activeListings: dedup(excludeLeases(activeData)).map(transformListing),
              soldListings: dedup(excludeLeases(soldData)).map(transformListing),
            };
          } else {
            // No MLS ID — fall back to name-based query on list_agent_full_name
            const nameFilter = `list_agent_full_name.ilike.%${agentName}%`;

            const activeStatusesName = ['Active', 'Coming Soon', 'Active Under Contract', 'Contingent', 'Pending', 'Pending Inspect/Feasib', 'Active U/C W/ Bump', 'To Be Built'];

            const [activeRes, soldRes] = await Promise.all([
              supabase
                .from('mls_properties')
                .select('*')
                .not('mls_number', 'is', null)
                .or(nameFilter)
                .in('status', activeStatusesName)
                .order('list_price', { ascending: false })
                .limit(200),
              supabase
                .from('mls_properties')
                .select('*')
                .not('mls_number', 'is', null)
                .or(nameFilter)
                .in('status', ['Closed', 'Sold'])
                .order('sold_price', { ascending: false, nullsFirst: false })
                .limit(200),
            ]);

            if (activeRes.error) console.error('Error fetching active MLS listings by name:', activeRes.error);
            if (soldRes.error) console.error('Error fetching sold MLS listings by name:', soldRes.error);

            return {
              activeListings: dedup(excludeLeases(activeRes.data || [])).map(transformListing),
              soldListings: dedup(excludeLeases(soldRes.data || [])).map(transformListing),
            };
          }
        })()
      : Promise.resolve({ activeListings: [] as MLSProperty[], soldListings: [] as MLSProperty[] }),

    // Realogy/SIR listings — by agent name
    agentName
      ? getRealogyListingsByAgentName(agentName)
      : Promise.resolve({ activeListings: [] as MLSProperty[], soldListings: [] as MLSProperty[] }),
  ]);

  // Combine both sources — enrich MLS listings with SIR media when matched
  const mergeWithSIRMedia = (mlsListings: MLSProperty[], sirListings: MLSProperty[]) => {
    const sirByKey = new Map<string, MLSProperty>();
    for (const listing of sirListings) {
      const key = `${listing.address}-${listing.city}-${listing.list_price}`.toLowerCase();
      sirByKey.set(key, listing);
    }

    const seen = new Set<string>();
    const result: MLSProperty[] = [];

    // MLS listings first, enriched with SIR media if matched
    for (const listing of mlsListings) {
      const key = `${listing.address}-${listing.city}-${listing.list_price}`.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);

      const sirMatch = sirByKey.get(key);
      if (sirMatch && sirMatch.photos.length > 0) {
        result.push({
          ...listing,
          photos: sirMatch.photos,
          virtual_tour_url: sirMatch.virtual_tour_url || listing.virtual_tour_url,
          video_urls: sirMatch.video_urls.length > 0 ? sirMatch.video_urls : listing.video_urls,
        });
      } else {
        result.push(listing);
      }
    }

    return result;
  };

  return {
    activeListings: mergeWithSIRMedia(mlsResult.activeListings, realogyResult.activeListings),
    soldListings: mergeWithSIRMedia(mlsResult.soldListings, realogyResult.soldListings),
  };
}

// Get the newest single family residential listings in a city (active statuses)
export async function getNewestSingleFamilyByCity(
  city: string,
  limit = 10
): Promise<MLSProperty[]> {
  if (!isSupabaseConfigured()) return [];

  const activeStatuses = [
    'Active',
    'Coming Soon',
    'Active Under Contract',
    'Contingent',
    'Pending',
    'Pending Inspect/Feasib',
    'Active U/C W/ Bump',
    'To Be Built',
  ];

  const { data, error } = await supabase
    .from('mls_properties')
    .select('*')
    .ilike('city', city)
    .in('status', activeStatuses)
    .or(
      'property_sub_type.eq.Single Family Residence,property_sub_type.eq.Site Built-Owned Lot,property_sub_type.eq.Residential,property_sub_type.is.null'
    )
    .not('property_type', 'in', '(Residential Lease,Commercial Lease,Res Vacant Land,RES Vacant Land,Commercial Sale)')
    .not('list_price', 'is', null)
    .order('listing_date', { ascending: false })
    .limit(limit * 2);

  if (error) {
    console.error('Error fetching newest single family by city:', error);
    return [];
  }

  const rows = (data || []).filter(
    (r: any) => !EXCLUDED_LEASE_TYPES.includes(r.property_type)
  );

  // Dedupe by listing_id
  const seen = new Set<string>();
  const unique: any[] = [];
  for (const row of rows) {
    const id = String((row.mls_number || row.listing_id) || row.id);
    if (seen.has(id)) continue;
    seen.add(id);
    unique.push(row);
    if (unique.length >= limit) break;
  }

  const listings = unique.map(transformListing);
  return enrichListingsWithSIRMedia(listings);
}

// Get all sold/closed listings where any of the provided agent IDs match
// in any role: list agent, co-list, buyer, or co-buyer.
// Used for team-wide "sold by" pages.
export async function getSoldListingsByAgentIds(agentIds: string[]): Promise<MLSProperty[]> {
  if (!isSupabaseConfigured() || agentIds.length === 0) return [];

  const ids = Array.from(new Set(agentIds.filter(Boolean)));
  if (ids.length === 0) return [];

  // Build OR filter: any of the IDs matching any role. mls_properties
  // has no co_buyer_agent_mls_id column, so we only check the three that
  // actually exist on the table.
  const orParts: string[] = [];
  for (const id of ids) {
    orParts.push(`list_agent_mls_id.eq.${id}`);
    orParts.push(`co_list_agent_mls_id.eq.${id}`);
    orParts.push(`buyer_agent_mls_id.eq.${id}`);
  }
  const filter = orParts.join(',');

  const { data, error } = await supabase
    .from('mls_properties')
    .select('*')
    .not('mls_number', 'is', null)
    .or(filter)
    .in('status', ['Closed', 'Sold'])
    .order('sold_date', { ascending: false, nullsFirst: false })
    .limit(1000);

  if (error) {
    console.error('Error fetching team sold listings:', error);
    return [];
  }

  const rows = (data || []).filter((r: any) => !EXCLUDED_LEASE_TYPES.includes(r.property_type));

  // Dedupe by listing_id, preferring rows with more complete data
  const byListingId = new Map<string, any>();
  for (const row of rows) {
    if (!(row.mls_number || row.listing_id)) continue;
    const lid = String((row.mls_number || row.listing_id));
    const existing = byListingId.get(lid);
    if (!existing) {
      byListingId.set(lid, row);
    } else {
      const existingScore = (existing.updated_at ? 100 : 0) + (existing.address ? 1 : 0) + (existing.media ? 1 : 0);
      const newScore = (row.updated_at ? 100 : 0) + (row.address ? 1 : 0) + (row.media ? 1 : 0);
      if (newScore > existingScore) byListingId.set(lid, row);
    }
  }

  return Array.from(byListingId.values()).map(transformListing);
}

/**
 * Fetch a small set of other active listings related to the current one —
 * used for the "More Properties Nearby" section on listing detail pages.
 * Scoped to the same city when possible, falling back to same state.
 * Excludes the current listing's MLS number.
 */
export async function getRelatedListings(
  current: { mls_number?: string | null; city?: string | null; state?: string | null },
  limit: number = 6
): Promise<MLSProperty[]> {
  if (!isSupabaseConfigured()) return [];
  const exclude = current.mls_number || '';

  // 1) Same city, Active
  if (current.city) {
    const { data } = await supabase
      .from('mls_properties')
      .select('*')
      .eq('status', 'Active')
      .ilike('city', current.city)
      .not('mls_number', 'is', null)
      .neq('listing_id', exclude)
      .order('list_price', { ascending: false, nullsFirst: false })
      .limit(limit);
    if (data && data.length > 0) return data.map(transformListing);
  }

  // 2) Same state fallback
  if (current.state) {
    const { data } = await supabase
      .from('mls_properties')
      .select('*')
      .eq('status', 'Active')
      .eq('state', current.state)
      .not('mls_number', 'is', null)
      .neq('listing_id', exclude)
      .order('list_price', { ascending: false, nullsFirst: false })
      .limit(limit);
    if (data && data.length > 0) return data.map(transformListing);
  }

  return [];
}

/**
 * MLS descriptions frequently arrive hard-wrapped — a line break at the end of
 * every ~80-90 character line (often CRLF), with blank lines separating the real
 * paragraphs. Rendering each source line as its own element (or via
 * whitespace-pre-wrap) makes the text look double-spaced and leaves stray
 * carriage returns behind. Normalize to clean paragraphs: strip CRs, reflow
 * soft-wrapped lines into spaces, and split on blank lines.
 */
export function toDescriptionParagraphs(raw?: string | null): string[] {
  if (!raw) return [];
  return raw
    .replace(/\r\n?/g, '\n')                // CRLF / lone CR -> LF
    .split(/\n{2,}/)                         // paragraphs = blank-line separated
    .map((p) =>
      p
        .replace(/\s*\n\s*/g, ' ')          // reflow soft-wrapped lines to spaces
        .replace(/[ \t]{2,}/g, ' ')         // collapse runs of whitespace
        .trim()
    )
    .filter(Boolean);
}
